import { cn } from "~/lib/utils";
import { Emdash } from "../Punctuation";
import { Tooltip } from "../Tooltip";
import { DataGridDollarValue } from "./DataGridDollarValue";
import { formatApy } from "~/utils/functions/formatApy";
import { GridPercentSign } from "./GridPercentSign";
import { isNullOrUndefined } from "~/utils/isNullOrUndefined";

// // // //

/**
 * DataGridPercentChangeArrow
 * Render a percent-change widget in DataGridCell
 */
function DataGridPercentChangeArrow(props: { value: number }) {
    const { value } = props;

    if (typeof value !== "number" || Number.isNaN(value)) {
        return <Emdash />;
    }

    if (value === 0) {
        return (
            <span className="text-secondary-foreground cursor-default invisible">
                {"△"}
            </span>
        );
    }

    const isPositive = value >= 0;
    const arrowIcon = isPositive ? "▲" : "▼";
    const colorClass = isPositive ? "text-change-up" : "text-change-down";

    return (
        <span className={cn(colorClass, "cursor-default")}>{arrowIcon}</span>
    );
}

export function DataGridDollarPercentChangeComboCell(props: {
    value: null | number;
    pct_change_value: null | number;
    pct_change_interval?: "7d" | "30d";
    decimal_places?: number;
}) {
    const {
        value,
        decimal_places,
        pct_change_value,
        pct_change_interval = "7d",
    } = props;

    // Cast value_percent_change to 0 if null/undefined
    const value_percent_change = isNullOrUndefined(pct_change_value)
        ? 0
        : (pct_change_value as number);

    // Render <Emdash/> if value is null
    if (value === null) {
        return <Emdash />;
    }

    // Render content without tooltip when change is 0
    if (value_percent_change === 0) {
        return (
            <span className="flex items-center">
                <DataGridDollarValue
                    value={value as number}
                    decimalPlaces={decimal_places}
                />
                <div className="ml-1.5">
                    <DataGridPercentChangeArrow value={value_percent_change} />
                </div>
            </span>
        );
    }

    return (
        <Tooltip
            placement="right"
            tooltipWrapperClassName={cn("text-white rounded-lg", {
                "bg-green-500 border-green-500": value_percent_change > 0,
                "bg-red-500 border-red-500": value_percent_change < 0,
                "bg-black border-black": value_percent_change === 0,
            })}
            tooltipContent={
                <div className="w-max px-2 py-1">
                    <p
                        className={cn(
                            "whitespace-nowrap text-xs tabular-nums text-center",
                            {
                                "text-foreground": value_percent_change === 0,
                            },
                            // value_percent_change >= 0 ? "text-change-up" : "text-change-down"
                        )}
                    >
                        {value_percent_change === 0 && "No Change"}
                        {value_percent_change !== 0 && (
                            // <DataGridPercentChangeValue
                            //     value={value_percent_change}
                            // />
                            <>
                                {value_percent_change >= 0 ? "+" : "-"}
                                &nbsp;
                                {parseFloat(
                                    Math.abs(
                                        formatApy(value_percent_change),
                                    ).toFixed(2),
                                ).toLocaleString()}
                                <GridPercentSign />
                                <span className="ml-0.5">
                                    {pct_change_interval === "7d"
                                        ? "from 7d ago"
                                        : "from 30d ago"}
                                </span>
                            </>
                        )}
                    </p>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <span className="flex items-center" ref={setTriggerRef}>
                    <DataGridDollarValue
                        value={value as number}
                        decimalPlaces={decimal_places}
                    />
                    <div className="ml-1.5">
                        <DataGridPercentChangeArrow
                            value={value_percent_change as number}
                        />
                    </div>
                </span>
            )}
        </Tooltip>
    );
}
